import type { StateTree } from 'pinia'

import { compressToUTF16, decompressFromUTF16 } from 'lz-string'

// Wrapper written in place of a compressed field's value; the key is unlikely to collide with
// real state and lets the deserializer recognise a compressed field from an uncompressed one.
const COMPRESSED_KEY = '__lz'

interface CompressedField {
  [COMPRESSED_KEY]: string
}

function isCompressedField(value: unknown): value is CompressedField {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as CompressedField)[COMPRESSED_KEY] === 'string'
  )
}

// `decompressFromUTF16` returns garbage rather than `null` for many corrupted inputs, so the parse
// can throw; a throw out of `deserialize` would hydrate the whole store to defaults.
function readCompressedField(value: CompressedField): unknown {
  try {
    const json = decompressFromUTF16(value[COMPRESSED_KEY])
    return json ? JSON.parse(json) : null
  } catch {
    return null
  }
}

/**
 * Builds a `pinia-plugin-persistedstate` serializer that LZ-compresses the named top-level state
 * fields and leaves the rest as plain JSON. `compressToUTF16` emits only characters localStorage
 * stores losslessly, so the result is written as-is.
 *
 * Existing entries written without compression still hydrate: an uncompressed field is left
 * untouched, and a compressed field that cannot be read hydrates as `null` rather than the whole
 * hydration failing.
 *
 * Usage: `persist: { serializer: createCompressedFieldsSerializer(['catalogContent']) }`
 */
export function createCompressedFieldsSerializer(fields: readonly string[]) {
  return {
    serialize: (state: StateTree): string => {
      const output: StateTree = { ...state }

      for (const field of fields) {
        const value = output[field]
        if (value === undefined || value === null) continue

        output[field] = { [COMPRESSED_KEY]: compressToUTF16(JSON.stringify(value)) }
      }

      return JSON.stringify(output)
    },
    deserialize: (raw: string): StateTree => {
      const state: StateTree = JSON.parse(raw)

      for (const field of fields) {
        const value = state[field]
        if (!isCompressedField(value)) continue

        state[field] = readCompressedField(value)
      }

      return state
    }
  }
}
